import type { BuildEffects, BuildSource, EquipmentMilestone, EquipmentSlot, HeroBuild, HeroStats, SaveState } from '../shared';
import { DEFAULT_EQUIPMENT, EQUIPMENT, EQUIPMENT_SYNERGIES, STARTER_EQUIPMENT, equipmentItem } from '../content/equipment/catalog';
import type { EconomySave } from './save';

export interface EquipmentSave { v: 1; owned: string[]; slots: Record<EquipmentSlot, string>; milestones: Partial<Record<EquipmentMilestone, number>> }
export const EQUIPMENT_SLOTS: readonly EquipmentSlot[] = ['weapon', 'armour', 'boots', 'charm'];
export const STAT_KEYS: readonly (keyof HeroStats)[] = ['maxHp', 'attack', 'maxStamina', 'startFervor', 'guardReduction', 'damageReduction'];
export const EFFECT_KEYS: readonly (keyof BuildEffects)[] = ['counterDamage', 'counterFervor', 'guardHeal', 'prayerBonus', 'firstHitShield'];
const record = (value: unknown): Record<string, unknown> => value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
const count = (value: unknown, max = 1000000): number => typeof value === 'number' && Number.isFinite(value) ? Math.max(0, Math.min(max, Math.floor(value))) : 0;
const uniqueStrings = (value: unknown): string[] => Array.isArray(value) ? [...new Set(value.filter((v): v is string => typeof v === 'string'))] : [];

/** Never trust arbitrary item IDs, cross-slot IDs, unowned items or externally saved derived stats. */
export function readEquipment(value: unknown): EquipmentSave {
  const raw = record(value);
  const owned = [...new Set([...STARTER_EQUIPMENT, ...uniqueStrings(raw.owned).filter(id => equipmentItem(id))])];
  const inputSlots = record(raw.slots), slots = { ...DEFAULT_EQUIPMENT } as Record<EquipmentSlot, string>;
  for (const slot of EQUIPMENT_SLOTS) {
    const id = inputSlots[slot];
    if (typeof id === 'string' && owned.includes(id) && equipmentItem(id)?.slot === slot) slots[slot] = id;
  }
  const milestones: EquipmentSave['milestones'] = {};
  const inputProgress = record(raw.milestones);
  for (const key of ['starter', 'arena', 'cards', 'road', 'crypt', 'order', 'race', 'chapter'] as const) milestones[key] = count(inputProgress[key]);
  return { v: 1, owned, slots, milestones };
}

export function validateEquipment(value: unknown): string[] {
  if (value === undefined) return []; // Pre-equipment saves remain valid.
  const raw = record(value), errors: string[] = [];
  if (raw.v !== 1) errors.push('equipment: invalid version');
  if (!Array.isArray(raw.owned) || raw.owned.some(id => typeof id !== 'string' || !equipmentItem(id))) errors.push('equipment: invalid owned item');
  const slots = record(raw.slots), owned = new Set(uniqueStrings(raw.owned));
  for (const slot of EQUIPMENT_SLOTS) {
    const id = slots[slot];
    if (typeof id !== 'string' || equipmentItem(id)?.slot !== slot || !owned.has(id)) errors.push(`equipment: invalid ${slot} slot`);
  }
  for (const n of Object.values(record(raw.milestones))) if (typeof n !== 'number' || !Number.isFinite(n) || n < 0 || n > 1000000 || !Number.isInteger(n)) errors.push('equipment: invalid progress');
  return errors;
}

/** Raw achievements, never economy road activity (which historically also counted an order claim). */
export function equipmentProgress(slices: SaveState['slices'], gear: EquipmentSave): Record<EquipmentMilestone, number> {
  const combat = record(slices.combat), cards = record(slices.cards), road = record(slices.exploration), economy = record(slices.economy);
  const claims = uniqueStrings(economy.claims), orderClaims = uniqueStrings(road.claims);
  const run = record(road.run);
  const progress = {
    starter: 1,
    arena: count(combat.wins) + count(combat.losses),
    cards: count(cards.played),
    road: count(road.completed),
    crypt: Math.max(claims.filter(id => id.startsWith('dungeon.clear:')).length, run.mode === 'crypt' && run.status === 'complete' && run.claimed === true ? 1 : 0),
    order: orderClaims.length,
    race: count(cards.raceWins), chapter: count(economy.chapters),
  };
  for (const key of Object.keys(progress) as EquipmentMilestone[]) progress[key] = Math.max(progress[key], gear.milestones[key] ?? 0);
  return progress;
}

export function newlyEarnedEquipment(slices: SaveState['slices'], gear: EquipmentSave): string[] {
  const progress = equipmentProgress(slices, gear);
  return EQUIPMENT.filter(i => !gear.owned.includes(i.id) && progress[i.unlock.milestone] >= i.unlock.count).map(i => i.id);
}

/** Single mathematical source consumed by both the dashboard and a frozen new-duel spec. */
export function deriveBuild(economy: EconomySave, candidate?: string): HeroBuild {
  const gear = readEquipment(economy.equipment), items = { ...gear.slots };
  const replacement = candidate ? equipmentItem(candidate) : undefined;
  if (replacement) items[replacement.slot] = replacement.id;
  const level = Math.max(1, count(economy.level, 50)), might = count(economy.training.might, 12), grit = count(economy.training.grit, 12), cunning = count(economy.training.cunning, 12);
  const sources: BuildSource[] = [
    { kind: 'base', id: 'base', stats: { maxHp: 100, attack: 17, maxStamina: 4, startFervor: 20, guardReduction: 2, damageReduction: 0 }, effects: {} },
    { kind: 'level', id: 'level', stats: { maxHp: (level - 1) * 4, attack: level - 1 }, effects: {} },
    { kind: 'training', id: 'might', stats: { attack: might * 2 }, effects: {} },
    { kind: 'training', id: 'grit', stats: { maxHp: grit * 6 }, effects: {} },
    { kind: 'training', id: 'cunning', stats: { guardReduction: cunning, damageReduction: Math.floor(cunning / 2) }, effects: {} },
    ...EQUIPMENT_SLOTS.map(slot => { const item = equipmentItem(items[slot])!; return { kind: 'item' as const, id: item.id, stats: { ...item.stats }, effects: { ...item.effects } }; }),
  ];
  const synergies = EQUIPMENT_SYNERGIES.filter(s => s.needs.every(id => Object.values(items).includes(id)));
  sources.push(...synergies.map(s => structuredClone(s.source)));
  const stats: HeroStats = { maxHp: 0, attack: 0, maxStamina: 0, startFervor: 0, guardReduction: 0, damageReduction: 0 };
  const effects: BuildEffects = { counterDamage: 0, counterFervor: 0, guardHeal: 0, prayerBonus: 0, firstHitShield: 0 };
  for (const source of sources) {
    for (const key of STAT_KEYS) stats[key] += source.stats[key] ?? 0;
    for (const key of EFFECT_KEYS) effects[key] += source.effects[key] ?? 0;
  }
  stats.maxHp = Math.max(40, Math.min(500, stats.maxHp)); stats.attack = Math.max(6, Math.min(100, stats.attack));
  stats.maxStamina = Math.max(2, Math.min(7, stats.maxStamina)); stats.startFervor = Math.max(0, Math.min(65, stats.startFervor));
  stats.guardReduction = Math.max(0, stats.guardReduction); stats.damageReduction = Math.max(0, stats.damageReduction);
  const weapon = equipmentItem(items.weapon)!;
  return { version: 1, weapon: weapon.weapon ?? 'sword', items, outfit: equipmentItem(items.armour)?.outfit ?? null, stats, effects,
    abilities: [...(weapon.abilities ?? [])], sources, synergies: synergies.map(s => s.id) };
}
